/**
 * HDB Portal — Public self-registration page for new clients.
 * ----------------------------------------------------------------
 *  • Mobile-first, single vertical scroll form
 *  • Household vs Business toggle (GSTIN slot appears for Business only)
 *  • Mobile number typed twice with live match indicator
 *  • Alt phone, email, and full delivery address
 *  • Auto-approve on submit → portal session issued → straight to /portal
 *  • Tagline: "What we deliver is savings. Rest, all is just delivery."
 */
import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import portalApi from "@/lib/portalApi";
import { safeNext } from "@/lib/plannerApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { toast } from "sonner";
import { Home, Building2, Mail, Phone, MapPin, FileText, Check, X,
         Loader2, ArrowRight, Sparkles, Store, KeyRound } from "lucide-react";

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{1}Z[0-9A-Z]{1}$/;

const HDB_LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_pdf-guide-dev/artifacts/7s3f15d0_HDB%20LOGO.webp";

const INDIAN_STATES = [
    ["27", "Maharashtra"],  ["24", "Gujarat"],        ["07", "Delhi"],
    ["09", "Uttar Pradesh"], ["06", "Haryana"],       ["03", "Punjab"],
    ["05", "Uttarakhand"],  ["08", "Rajasthan"],      ["29", "Karnataka"],
    ["33", "Tamil Nadu"],   ["36", "Telangana"],      ["19", "West Bengal"],
    ["23", "Madhya Pradesh"], ["10", "Bihar"],        ["21", "Odisha"],
    ["30", "Goa"],          ["02", "Himachal Pradesh"], ["01", "Jammu & Kashmir"],
    ["11", "Sikkim"],       ["12", "Arunachal Pradesh"], ["13", "Nagaland"],
    ["14", "Manipur"],      ["15", "Mizoram"],        ["16", "Tripura"],
    ["17", "Meghalaya"],    ["18", "Assam"],          ["20", "Jharkhand"],
    ["22", "Chhattisgarh"], ["25", "Daman & Diu"],    ["26", "Dadra & Nagar Haveli"],
    ["28", "Andhra Pradesh"], ["31", "Lakshadweep"],  ["32", "Kerala"],
    ["34", "Puducherry"],   ["35", "A & N Islands"],  ["37", "Ladakh"],
    ["38", "Other Territory"],
];

const cleanPhone = (v) => (v || "").replace(/\D/g, "").slice(-10);
const cleanMpin  = (v) => (v || "").replace(/\D/g, "").slice(0, 4);
// MPINs the backend also blocks — filter early so the user doesn't waste a submit.
const WEAK_MPINS = new Set(["0000","1111","2222","3333","4444","5555","6666","7777","8888","9999","1234","4321"]);

export default function PortalRegister() {
    const nav = useNavigate();
    const { refresh } = usePortalAuth();
    const [type, setType] = useState("household");  // "household" | "business"
    const [f, setF] = useState({
        name: "", gstin: "",
        contact_email: "", contact_phone: "", contact_phone2: "",
        alt_phone: "",
        address: "", city: "", pincode: "",
        state: "Maharashtra", state_code: "27",
        mpin: "", mpin2: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

    const phone = cleanPhone(f.contact_phone);
    const phone2 = cleanPhone(f.contact_phone2);
    const phoneMatches = phone && phone2 && phone === phone2;
    const phoneMismatch = phone && phone2 && phone !== phone2;
    const validPhone = phone.length === 10 && /^[6-9]/.test(phone);
    const validGstin = !f.gstin || GSTIN_RE.test((f.gstin || "").toUpperCase());
    const validName = f.name.trim().length >= 2;
    const mpin = cleanMpin(f.mpin);
    const mpin2 = cleanMpin(f.mpin2);
    const validMpinShape = mpin.length === 4;
    const strongMpin = validMpinShape && !WEAK_MPINS.has(mpin);
    const mpinMatches = validMpinShape && mpin === mpin2;
    const mpinMismatch = mpin.length === 4 && mpin2.length === 4 && mpin !== mpin2;

    const canSubmit = validName && validPhone && phoneMatches && validGstin
                      && strongMpin && mpinMatches;
    const missingReason = useMemo(() => {
        if (!validName) return "Add your name (or business name)";
        if (!validPhone) return "Enter a valid 10-digit Indian mobile number";
        if (!phoneMatches) return "Both mobile numbers must match";
        if (!validGstin) return "GSTIN doesn't look right — 15 characters like 27AAAAA1234A1Z5. You can leave it blank.";
        if (!validMpinShape) return "Set a 4-digit MPIN — you'll use this to sign back in later";
        if (!strongMpin) return "That MPIN is too easy to guess — try a different 4-digit combination";
        if (!mpinMatches) return "Both MPIN entries must match";
        return "";
    }, [validName, validPhone, phoneMatches, validGstin, validMpinShape, strongMpin, mpinMatches]);

    // ------------- Lead auto-save --------------------------------------
    // Fire /portal/lead 800ms after the last keystroke, but ONLY when the
    // primary phone is a valid 10-digit Indian mobile.  Without a valid
    // phone there's nothing to follow up on.  Sending the payload without
    // the MPIN or the confirm-phone means the admin can see how far the
    // customer got before dropping off.
    const autosaveTimer = useRef(null);
    const lastPushedRef = useRef("");     // last serialized payload we sent
    const submittedRef = useRef(false);   // don't overwrite after successful signup
    useEffect(() => {
        if (submittedRef.current) return;
        if (!validPhone) return;
        const step = !validName ? "basics"
                   : !phoneMatches ? "phone"
                   : !strongMpin ? "mpin"
                   : (f.address || "").trim().length < 8 ? "address"
                   : "ready";
        const payload = {
            contact_phone: phone,
            name: f.name.trim(),
            gstin: (f.gstin || "").trim().toUpperCase(),
            contact_email: (f.contact_email || "").trim(),
            alt_phone: cleanPhone(f.alt_phone),
            address: (f.address || "").trim(),
            city: (f.city || "").trim(),
            pincode: (f.pincode || "").trim(),
            state: f.state,
            state_code: f.state_code,
            customer_type: type,
            has_mpin: mpin.length === 4,
            has_address: (f.address || "").trim().length >= 8,
            step,
        };
        const serialized = JSON.stringify(payload);
        if (serialized === lastPushedRef.current) return;
        clearTimeout(autosaveTimer.current);
        autosaveTimer.current = setTimeout(() => {
            lastPushedRef.current = serialized;
            // Best-effort — silently swallow errors so a flaky network
            // never blocks the signup flow itself.
            portalApi.post("/lead", payload).catch(() => {});
        }, 800);
        return () => clearTimeout(autosaveTimer.current);
    }, [f, phone, type, mpin, validPhone, validName, phoneMatches, strongMpin]);
    // -------------------------------------------------------------------

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) { toast.error(missingReason); return; }
        setSubmitting(true);
        try {
            const body = {
                name: f.name.trim(),
                gstin: (f.gstin || "").trim().toUpperCase(),
                contact_email: (f.contact_email || "").trim(),
                contact_phone: phone,
                alt_phone: cleanPhone(f.alt_phone),
                address: (f.address || "").trim(),
                city: (f.city || "").trim(),
                pincode: (f.pincode || "").trim(),
                state: f.state, state_code: f.state_code,
                customer_type: type,
                mpin,
            };
            const r = await portalApi.post("/register", body);
            submittedRef.current = true;   // stop autosave from re-firing
            await refresh();
            toast.success(r.data.message || "Welcome! Let's get you your first order.");
            nav(safeNext(new URLSearchParams(window.location.search).get("next")), { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Registration failed");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-emerald-50/40"
             data-testid="portal-register-page">
            {/* Top strip — service-area disclosure + sign-in link.
                Rendered before the hero so it's the first thing anyone
                who's landed on the wrong page sees, avoiding wasted signups
                from out-of-service cities. */}
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 text-center"
                 data-testid="portal-register-service-area-banner">
                <div className="max-w-lg mx-auto flex flex-col sm:flex-row items-center justify-center gap-x-3 gap-y-1 text-[12px]">
                    <span className="font-black text-amber-900 uppercase tracking-wide">
                        📍 Delivering in Dehradun &amp; Bangalore
                    </span>
                </div>
                <div className="text-[11px] text-slate-600 mt-1">
                    Already registered?{" "}
                    <Link to="/portal/signin" className="font-bold text-emerald-700 underline"
                          data-testid="portal-register-signin-top">
                        Sign in with MPIN
                    </Link>
                </div>
            </div>

            {/* Hero */}
            <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 text-white
                            px-5 pt-8 pb-10 rounded-b-[36px] shadow-lg relative overflow-hidden">
                <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 blur-2xl"/>
                <div className="absolute -left-8 -bottom-8 w-40 h-40 rounded-full bg-yellow-300/10 blur-2xl"/>
                <div className="relative">
                    <div className="flex items-center gap-3">
                        <div className="w-14 h-14 rounded-2xl bg-white p-2 shadow-lg grid place-items-center shrink-0">
                            <img src={HDB_LOGO} alt="Har Din Bachat"
                                 className="w-full h-full object-contain"
                                 data-testid="portal-register-logo"/>
                        </div>
                        <div>
                            <div className="text-lg font-black tracking-tight leading-tight"
                                 style={{fontFamily:"'Chivo',sans-serif"}}>
                                Har Din Bachat
                            </div>
                            <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">
                                HDB Retail · Trusted since day one
                            </div>
                        </div>
                    </div>
                    <h1 className="text-3xl sm:text-4xl font-black leading-tight mt-6"
                        style={{fontFamily:"'Chivo',sans-serif"}}>
                        Sign up in a minute.
                    </h1>
                    <p className="text-emerald-50/95 mt-2 text-sm italic font-medium">
                        “What we deliver is savings. Rest, all is just delivery.”
                    </p>
                    <div className="mt-4 flex items-center gap-1.5 flex-wrap text-[11px]">
                        <span className="bg-white/15 backdrop-blur px-2 py-0.5 rounded-full">🏠 Home delivery</span>
                        <span className="bg-white/15 backdrop-blur px-2 py-0.5 rounded-full">🎙️ Voice order</span>
                        <span className="bg-white/15 backdrop-blur px-2 py-0.5 rounded-full">✅ No account fee</span>
                    </div>
                    {/* Promise line — replaces the "100+ items" chip */}
                    <div className="mt-3 text-[13px] font-semibold text-emerald-50/95 leading-snug">
                        Any and every monthly essential<br/>
                        <span className="text-white/90">delivered right to your doorstep.</span>
                    </div>
                </div>
            </div>

            <form onSubmit={submit}
                  className="max-w-lg mx-auto px-5 -mt-4 pb-32 space-y-5">

                {/* Type selector — the very first choice */}
                <section className="bg-white rounded-2xl shadow-md border border-slate-200 p-4">
                    <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 block mb-3">
                        I am a…
                    </label>
                    <div className="grid grid-cols-2 gap-2.5">
                        <button type="button" onClick={() => setType("household")}
                                className={`p-4 rounded-xl border-2 transition-all active:scale-[0.98] text-left
                                            ${type === "household"
                                              ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200"
                                              : "border-slate-200 bg-white hover:bg-slate-50"}`}
                                data-testid="portal-register-type-household">
                            <Home size={20} className={type === "household" ? "text-emerald-700" : "text-slate-500"}/>
                            <div className="font-black text-slate-900 mt-2">Regular household</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">For daily home grocery</div>
                        </button>
                        <button type="button" onClick={() => setType("business")}
                                className={`p-4 rounded-xl border-2 transition-all active:scale-[0.98] text-left
                                            ${type === "business"
                                              ? "border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200"
                                              : "border-slate-200 bg-white hover:bg-slate-50"}`}
                                data-testid="portal-register-type-business">
                            <Building2 size={20} className={type === "business" ? "text-emerald-700" : "text-slate-500"}/>
                            <div className="font-black text-slate-900 mt-2">Business / Institution</div>
                            <div className="text-[10px] text-slate-500 mt-0.5">Shop · School · Office · Hotel</div>
                        </button>
                    </div>
                </section>

                {/* Basics */}
                <section className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 space-y-3.5">
                    <Label>Your {type === "business" ? "business" : "full"} name</Label>
                    <BigInput
                        value={f.name}
                        onChange={(e) => set("name", e.target.value)}
                        placeholder={type === "business" ? "e.g. Cambrian Hall School" : "e.g. Rohit Sharma"}
                        icon={type === "business" ? <Building2 size={16}/> : <Home size={16}/>}
                        data-testid="portal-register-name"
                        autoFocus
                    />

                    {type === "business" && (
                        <>
                            <Label optional>GSTIN <span className="opacity-60 font-normal">— skip if you don't have one yet</span></Label>
                            <BigInput
                                value={f.gstin}
                                onChange={(e) => set("gstin", e.target.value.toUpperCase())}
                                placeholder="27AAAAA1234A1Z5"
                                icon={<FileText size={16}/>}
                                data-testid="portal-register-gstin"
                                maxLength={15}
                                error={f.gstin && !validGstin ? "Doesn't look like a valid GSTIN — you can leave it blank and update later" : ""}
                                good={f.gstin && validGstin ? "Valid GSTIN — B2B pricing will apply" : ""}
                            />
                        </>
                    )}
                </section>

                {/* Phone (dual-entry) */}
                <section className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 space-y-3.5">
                    <Label>Your mobile number</Label>
                    <BigInput
                        value={f.contact_phone}
                        onChange={(e) => set("contact_phone", cleanPhone(e.target.value))}
                        placeholder="10-digit number"
                        icon={<Phone size={16}/>}
                        prefix="+91"
                        data-testid="portal-register-phone"
                        inputMode="numeric"
                        maxLength={10}
                        error={f.contact_phone && !validPhone ? "Enter a valid 10-digit number starting with 6/7/8/9" : ""}
                    />
                    <Label>Confirm mobile number</Label>
                    <BigInput
                        value={f.contact_phone2}
                        onChange={(e) => set("contact_phone2", cleanPhone(e.target.value))}
                        placeholder="Re-enter the same number"
                        icon={<Phone size={16}/>}
                        prefix="+91"
                        data-testid="portal-register-phone2"
                        inputMode="numeric"
                        maxLength={10}
                        onPaste={(e) => { e.preventDefault(); toast.error("Please type the number again — paste is disabled to prevent mistakes."); }}
                        error={phoneMismatch ? "Numbers don't match" : ""}
                        good={phoneMatches ? "Numbers match ✓" : ""}
                    />
                    <Label optional>Alternate mobile number</Label>
                    <BigInput
                        value={f.alt_phone}
                        onChange={(e) => set("alt_phone", cleanPhone(e.target.value))}
                        placeholder="Optional · WhatsApp number OK"
                        icon={<Phone size={16}/>}
                        prefix="+91"
                        data-testid="portal-register-alt-phone"
                        inputMode="numeric"
                        maxLength={10}
                    />
                    <Label optional>Email</Label>
                    <BigInput
                        value={f.contact_email}
                        onChange={(e) => set("contact_email", e.target.value)}
                        placeholder="Optional · for order emails"
                        icon={<Mail size={16}/>}
                        data-testid="portal-register-email"
                        type="email"
                    />
                </section>

                {/* MPIN — customer sets their sign-in code at signup */}
                <section className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 space-y-3.5"
                         data-testid="portal-register-mpin-section">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 grid place-items-center">
                            <KeyRound size={16} className="text-emerald-700"/>
                        </div>
                        <div>
                            <Label>Create a 4-digit MPIN</Label>
                            <div className="text-[11px] text-slate-500 mt-0.5">
                                You'll use this + your mobile number to sign back in from any device.
                            </div>
                        </div>
                    </div>
                    <BigInput
                        value={f.mpin}
                        onChange={(e) => set("mpin", cleanMpin(e.target.value))}
                        placeholder="• • • •"
                        icon={<KeyRound size={16}/>}
                        inputMode="numeric"
                        maxLength={4}
                        type="password"
                        autoComplete="new-password"
                        data-testid="portal-register-mpin"
                        error={f.mpin && !validMpinShape ? "Enter exactly 4 digits" :
                               (validMpinShape && !strongMpin ? "Too easy to guess — try another combination" : "")}
                        good={strongMpin ? "Looks good" : ""}
                    />
                    <Label>Confirm 4-digit MPIN</Label>
                    <BigInput
                        value={f.mpin2}
                        onChange={(e) => set("mpin2", cleanMpin(e.target.value))}
                        placeholder="Re-enter the same 4 digits"
                        icon={<KeyRound size={16}/>}
                        inputMode="numeric"
                        maxLength={4}
                        type="password"
                        autoComplete="new-password"
                        onPaste={(e) => { e.preventDefault();
                            toast.error("Please type the MPIN again — paste is disabled to prevent mistakes."); }}
                        data-testid="portal-register-mpin2"
                        error={mpinMismatch ? "MPIN entries don't match" : ""}
                        good={mpinMatches ? "MPIN entries match ✓" : ""}
                    />
                </section>

                {/* Address */}
                <section className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 space-y-3.5">
                    <Label>Delivery address</Label>
                    <div className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2"
                         data-testid="portal-register-address-reminder">
                        <MapPin size={13} className="mt-0.5 shrink-0"/>
                        <span>We currently deliver in <b>Dehradun and Bangalore</b>. Please add a delivery address in one of these cities.</span>
                    </div>
                    <textarea
                        value={f.address}
                        onChange={(e) => set("address", e.target.value)}
                        placeholder="House / flat number, street, landmark…"
                        rows={3}
                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none text-base"
                        data-testid="portal-register-address"
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label>City</Label>
                            <BigInput
                                value={f.city}
                                onChange={(e) => set("city", e.target.value)}
                                placeholder="Dehradun / Bangalore"
                                icon={<MapPin size={16}/>}
                                data-testid="portal-register-city"
                            />
                        </div>
                        <div>
                            <Label>Pincode</Label>
                            <BigInput
                                value={f.pincode}
                                onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
                                placeholder="248001"
                                inputMode="numeric" maxLength={6}
                                data-testid="portal-register-pincode"
                            />
                        </div>
                    </div>
                    <div>
                        <Label>State</Label>
                        <select
                            value={f.state_code}
                            onChange={(e) => {
                                const found = INDIAN_STATES.find(([code]) => code === e.target.value);
                                if (found) setF(p => ({...p, state_code: found[0], state: found[1]}));
                            }}
                            className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-emerald-500 outline-none text-base bg-white"
                            data-testid="portal-register-state">
                            {INDIAN_STATES.map(([code, name]) => (
                                <option key={code} value={code}>{name}</option>
                            ))}
                        </select>
                    </div>
                </section>

                {/* Existing user hint — kept at the bottom too as a safety
                    net for anyone who scrolled past the top strip. */}
                <div className="text-center text-xs text-slate-500">
                    Already registered?{" "}
                    <Link to="/portal/signin" className="font-bold text-emerald-700 underline"
                          data-testid="portal-register-signin-bottom">Sign in with MPIN</Link>
                </div>

                {/* Sticky footer submit */}
                <div className="fixed inset-x-0 bottom-0 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3 shadow-2xl"
                     data-testid="portal-register-footer">
                    <div className="max-w-lg mx-auto flex items-center gap-2">
                        <div className="flex-1 min-w-0 text-[11px] text-slate-500 truncate">
                            {canSubmit
                                ? <span className="text-emerald-700 font-bold flex items-center gap-1"><Sparkles size={11}/> Ready! Tap to start ordering.</span>
                                : missingReason}
                        </div>
                        <button type="submit" disabled={!canSubmit || submitting}
                                className={`px-6 py-3 rounded-xl font-black text-white text-sm shadow-lg transition-all
                                            ${canSubmit && !submitting
                                              ? "bg-gradient-to-br from-emerald-600 to-emerald-700 hover:from-emerald-500 active:scale-[0.98]"
                                              : "bg-slate-300 cursor-not-allowed"}`}
                                data-testid="portal-register-submit">
                            {submitting
                                ? <><Loader2 size={14} className="inline animate-spin mr-1"/> Signing you up…</>
                                : <>Register & start ordering <ArrowRight size={14} className="inline ml-1"/></>}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}


function Label({ children, optional }) {
    return (
        <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-2">
            {children}
            {optional && <span className="text-[9px] text-slate-400 font-bold normal-case tracking-normal">· optional</span>}
        </label>
    );
}


function BigInput({ icon, prefix, error, good, ...rest }) {
    return (
        <div>
            <div className={`flex items-center gap-2 px-3 rounded-xl border-2 transition-colors bg-white
                            ${error ? "border-red-400 focus-within:border-red-500"
                              : good ? "border-emerald-400 focus-within:border-emerald-500"
                              : "border-slate-200 focus-within:border-emerald-500"}`}>
                {icon && <span className="text-slate-400 shrink-0">{icon}</span>}
                {prefix && <span className="text-slate-400 font-mono text-sm">{prefix}</span>}
                <input className="flex-1 py-3 outline-none text-base bg-transparent min-w-0" {...rest}/>
                {good && !error && <Check className="text-emerald-600 shrink-0" size={16}/>}
                {error && <X className="text-red-500 shrink-0" size={16}/>}
            </div>
            {error && <div className="mt-1 text-[11px] text-red-600 font-semibold">{error}</div>}
            {good && !error && <div className="mt-1 text-[11px] text-emerald-700 font-semibold">{good}</div>}
        </div>
    );
}
